import React, { useState, useEffect } from 'react';
import { Card, CardBody, Row, Col, Modal, Button } from 'react-bootstrap';
import DataTable, { TableColumn } from "react-data-table-component";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faSave, faTimes, faSpinner } from '@fortawesome/free-solid-svg-icons';
import ButtonComponent from '../../../components/ButtonComponent';
import ButtonComponentV2 from '../../../components/ButtonComponentV2';
import ControlledTextField from '../../../components/ControlledTextField';
import ControlledSelect from '../../../components/ControlledSelect';
import EditButtonComponent from '../../../components/EditButtonComponent';
import TableLoadingComponent from '../../../components/TableLoadingComponent';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import Swal from "sweetalert2";
import {
  useCreateFeatureMutation,
  useUpdateFeatureMutation,
  useDeleteFeatureMutation,
  useGetAllFeaturesQuery,
  useGetFeatureGroupsQuery,
  FeatureItem,
  FeatureGroupItem
} from '../../../redux/services/rolesPermissionService';

interface FeatureFormData {
  displayName: string;
  description: string;
  featureGroupId: { value: string; label: string };
}

const Features = () => {
  const [showModal, setShowModal] = useState(false);
  const [editingFeature, setEditingFeature] = useState<FeatureItem | null>(null);
  const [apiFeatures, setApiFeatures] = useState<any[]>([]);
  const [apiFeatureGroups, setApiFeatureGroups] = useState<FeatureGroupItem[]>([]);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [deletingFeatureId, setDeletingFeatureId] = useState<string | null>(null);

  const [createFeature, { isLoading: isCreating }] = useCreateFeatureMutation();
  const [updateFeature, { isLoading: isUpdating }] = useUpdateFeatureMutation();
  const [deleteFeature, { isLoading: isDeleting }] = useDeleteFeatureMutation();
  const { data: featuresData, isFetching, refetch: refetchFeatures } = useGetAllFeaturesQuery();
  const { data: featureGroupsData } = useGetFeatureGroupsQuery();

  const isLoading = isCreating || isUpdating || isDeleting || isFetching;

  const { control, handleSubmit, reset, setValue, formState: { errors } } = useForm<FeatureFormData>();

  // Sync local state from cached queries
  useEffect(() => {
    if (featuresData) {
      const normalizedFeatures = featuresData.map((feature: any) => ({
        ...feature,
        isActive: feature.isActive !== undefined ? feature.isActive : true,
        featureGroupId: feature.featureGroupId || '',
      }));
      setApiFeatures(normalizedFeatures);
    }
  }, [featuresData]);

  useEffect(() => {
    if (featureGroupsData?.featureGroups) {
      setApiFeatureGroups(featureGroupsData.featureGroups);
    }
  }, [featureGroupsData]);

  const handleChangeRowsPerPage = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
  };

  // Create options for the feature group select
  const groupOptions = apiFeatureGroups.map(group => ({
    value: group.id,
    label: group.displayName || group.name || ''
  }));

  // Helper function to get group name by ID
  const getGroupName = (groupId?: string) => {
    if (!groupId) return 'No Group';
    const group = apiFeatureGroups.find(g => g.id === groupId);
    return group ? (group.displayName || group.name || 'Unknown Group') : 'Unknown Group';
  };

  const handleAddFeature = () => {
    setEditingFeature(null);
    reset();
    setShowModal(true);
  };

  const handleEditFeature = (feature: FeatureItem) => {
    setEditingFeature(feature);
    setValue('displayName', feature.displayName);
    setValue('description', feature.description);
    const group = apiFeatureGroups.find(g => g.id === feature.featureGroupId);
    setValue('featureGroupId', group ? { value: group.id, label: group.displayName || group.name || '' } : { value: '', label: '' });
    setShowModal(true);
  };

  const handleDeleteFeature = async (featureId: string) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#590B21",
      cancelButtonColor: "#ddd",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setDeletingFeatureId(featureId);
        try {
          await deleteFeature({
            featureId: featureId
          }).unwrap();
          // Cache invalidation will refetch; optimistically update local state
          setApiFeatures(prev => prev.filter(feature => feature.id !== featureId));
          Swal.fire("Deleted!", "Feature has been deleted.", "success");
          setDeletingFeatureId(null);
        } catch (error) {
          console.error('Error deleting feature:', error);
          Swal.fire(
            "Error!",
            "An error occurred while deleting the feature.",
            "error"
          );
          setDeletingFeatureId(null);
        }
      }
    });
  };

  const onSubmit = async (data: FeatureFormData) => {
    try {
      if (editingFeature) {
        // Update existing feature using RTK mutation
        await updateFeature({
          id: editingFeature.id,
          featureGroupId: data.featureGroupId.value,
          displayName: data.displayName,
          description: data.description
        }).unwrap();
        setShowModal(false);
        await refetchFeatures();
        toast.success('Feature updated successfully');
      } else {
        // Create new feature using RTK mutation
        await createFeature({
          featureGroupId: data.featureGroupId.value,
          displayName: data.displayName,
          description: data.description
        }).unwrap();
        setShowModal(false);
        await refetchFeatures();
        toast.success('Feature created successfully');
      }
      setShowModal(false);
      reset();
    } catch (error: any) {
      console.error('Error:', error);
      if (error.status === 409) {
        toast.error(error.data)
      }
      else {
        toast.error('Something went wrong');
      }
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    reset();
    setEditingFeature(null);
  };

  const columns: TableColumn<FeatureItem>[] = [
    {
      name: <h6 className="fw-bold">#</h6>,
      cell: (row, index) => <span>{index + 1}</span>,
      sortable: false,
      width: '80px',
    },
    {
      name: <h6 className="fw-bold">Feature Name</h6>,
      selector: (row) => row.displayName,
      cell: (row) => <strong>{row.displayName}</strong>,
      sortable: true,
    },
    {
      name: <h6 className="fw-bold">Description</h6>,
      selector: (row) => row.description,
      cell: (row) => <span>{row.description}</span>,
      sortable: true,
    },
    {
      name: <h6 className="fw-bold">Feature Group</h6>,
      cell: (row) => (
        <span className="badge bg-info">
          {getGroupName(row.featureGroupId)}
        </span>
      ),
      sortable: false,
    },
    {
      name: <h6 className="fw-bold">Status</h6>,
      cell: (row) => (
        <span className={`badge ${row.isActive ? 'bg-success' : 'bg-secondary'}`}>
          {row.isActive ? 'Active' : 'Inactive'}
        </span>
      ),
      sortable: false,
    },
    {
      name: <h6 className="fw-bold">Created Date</h6>,
      cell: (row) => {
        const createdDate = row.createdAt?._seconds
          ? new Date(row.createdAt._seconds * 1000).toLocaleDateString()
          : 'N/A';
        return <span>{createdDate}</span>;
      },
      sortable: true,
    },
    {
      name: <h6 className="fw-bold">Actions</h6>,
      cell: (row) => (
        <div className="d-flex gap-2">
          <EditButtonComponent
            onClick={() => handleEditFeature(row)}
          />
          {deletingFeatureId === row.id ? (
            <Button variant="light" disabled={true}>
              <FontAwesomeIcon icon={faSpinner} spin />
            </Button>
          ) : (
            <Button variant="light" onClick={() => handleDeleteFeature(row.id)}>
              <FontAwesomeIcon icon={faTrash} title="Delete" color="#F24E1E" />
            </Button>
          )}
        </div>
      ),
      sortable: false,
      right: true,
    },
  ];

  return (
    <div>
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center px-4">
            <h4>Features Management</h4>
            <ButtonComponentV2
              titel="Add Feature"
              icon={<FontAwesomeIcon icon={faPlus} />}
              onClick={handleAddFeature}
              color="#fff"
              backgroundColor="#590B21"
            />
          </div>
        </Col>
      </Row>

      <Row>
        <Col>
          <Card>
            <CardBody>
              {isFetching ? (<TableLoadingComponent numColumns={4} />) : (
                <DataTable
                  columns={columns}
                  data={apiFeatures}
                  responsive
                  pagination
                  highlightOnHover
                  pointerOnHover
                  paginationPerPage={rowsPerPage}
                  onChangeRowsPerPage={handleChangeRowsPerPage}
                  noDataComponent={<div>No features found. Click "Add Feature" to create one.</div>}
                  progressPending={isFetching}
                  progressComponent={<div><TableLoadingComponent numColumns={6} /></div>}
                />)}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Add/Edit Feature Modal */}
      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editingFeature ? 'Edit Feature' : 'Add New Feature'}</Modal.Title>
        </Modal.Header>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Modal.Body>
            <Row className="mb-3">
              <Col>
                <ControlledTextField
                  label="Feature Name"
                  placeholder="Enter feature name"
                  inputName="displayName"
                  inputType="text"
                  control={control}
                  rules={{
                    required: 'Feature name is required',
                    minLength: {
                      value: 2,
                      message: 'Feature name must be at least 2 characters'
                    }
                  }}
                />
                {errors.displayName && (
                  <div className="text-danger mt-1">{errors.displayName.message}</div>
                )}
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <ControlledTextField
                  label="Description"
                  placeholder="Enter feature description"
                  inputName="description"
                  inputType="text"
                  control={control}
                  rules={{
                    required: 'Description is required',
                    minLength: {
                      value: 5,
                      message: 'Description must be at least 5 characters'
                    }
                  }}
                />
                {errors.description && (
                  <div className="text-danger mt-1">{errors.description.message}</div>
                )}
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <ControlledSelect
                  label="Feature Group"
                  placeholder="Select feature group"
                  inputName="featureGroupId"
                  isSearchable={true}
                  control={control}
                  optionsList={groupOptions}
                  maxMenuHeight={150}
                  menuPlacement="auto"
                  menuPosition="fixed"
                  rules={{
                    required: 'Feature group is required'
                  }}
                />
                {errors.featureGroupId && (
                  <div className="text-danger mt-1">{errors.featureGroupId.message}</div>
                )}
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <ButtonComponent
              titel="Cancel"
              icon={<FontAwesomeIcon icon={faTimes} />}
              onClick={handleCloseModal}
              isDisabled={isLoading}
            />
            <ButtonComponent
              titel={isLoading ? "Saving..." : "Save"}
              icon={<FontAwesomeIcon icon={faSave} />}
              type="submit"
              isDisabled={isLoading}
            />
          </Modal.Footer>
        </form>
      </Modal>
    </div>
  );
};

export default Features;
